import React, { Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, TruckIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
const SupplyChainOverviewDashboard: React.FC = () => {
  const kpis = [{
    title: 'OTIF %',
    value: '89.2%',
    change: '+2.8%',
    isPositive: true,
    subtitle: 'On-Time In-Full delivery'
  }, {
    title: 'Supplier Fill Rate %',
    value: '94.5%',
    change: '+1.5%',
    isPositive: true,
    subtitle: 'Order fulfillment rate'
  }, {
    title: 'Avg Lead Time',
    value: '18.5 days',
    change: '-2.3 days',
    isPositive: true,
    subtitle: 'End-to-end lead time'
  }];
  // Lead Time Trend Data
  const leadTimeTrendData = [{
    month: 'Jan',
    avgLeadTime: 22.5,
    domesticLeadTime: 15.2,
    importLeadTime: 35.8
  }, {
    month: 'Feb',
    avgLeadTime: 21.8,
    domesticLeadTime: 14.8,
    importLeadTime: 34.5
  }, {
    month: 'Mar',
    avgLeadTime: 21.2,
    domesticLeadTime: 14.5,
    importLeadTime: 33.8
  }, {
    month: 'Apr',
    avgLeadTime: 20.5,
    domesticLeadTime: 14.2,
    importLeadTime: 32.5
  }, {
    month: 'May',
    avgLeadTime: 19.8,
    domesticLeadTime: 13.8,
    importLeadTime: 31.2
  }, {
    month: 'Jun',
    avgLeadTime: 19.2,
    domesticLeadTime: 13.5,
    importLeadTime: 30.5
  }, {
    month: 'Jul',
    avgLeadTime: 18.8,
    domesticLeadTime: 13.2,
    importLeadTime: 29.8
  }, {
    month: 'Aug',
    avgLeadTime: 18.5,
    domesticLeadTime: 13.0,
    importLeadTime: 29.2
  }, {
    month: 'Sep',
    avgLeadTime: 18.5,
    domesticLeadTime: 12.8,
    importLeadTime: 29.5
  }];
  // Zone-wise Logistics Performance Data
  const zoneLogisticsData = [{
    zone: 'North',
    otif: 91.5,
    fillRate: 95.2,
    leadTime: 12.5,
    cost: 285
  }, {
    zone: 'West',
    otif: 89.8,
    fillRate: 94.8,
    leadTime: 14.2,
    cost: 312
  }, {
    zone: 'South',
    otif: 88.5,
    fillRate: 93.5,
    leadTime: 16.8,
    cost: 345
  }, {
    zone: 'East',
    otif: 87.2,
    fillRate: 94.2,
    leadTime: 18.5,
    cost: 368
  }];
  // Supply Chain Performance Summary Table
  const performanceSummaryData = [{
    category: 'Raw Materials',
    suppliers: 125,
    otif: 91.2,
    fillRate: 95.8,
    avgLeadTime: 15.2,
    onTimeDelivery: 93.5,
    qualityScore: 96.2,
    costPerUnit: 142,
    inventoryDays: 28,
    riskScore: 'Low'
  }, {
    category: 'Components',
    suppliers: 85,
    otif: 88.5,
    fillRate: 93.2,
    avgLeadTime: 18.8,
    onTimeDelivery: 90.2,
    qualityScore: 94.8,
    costPerUnit: 285,
    inventoryDays: 35,
    riskScore: 'Medium'
  }, {
    category: 'Sub-Assemblies',
    suppliers: 42,
    otif: 89.8,
    fillRate: 94.5,
    avgLeadTime: 22.5,
    onTimeDelivery: 91.8,
    qualityScore: 95.5,
    costPerUnit: 425,
    inventoryDays: 32,
    riskScore: 'Low'
  }, {
    category: 'Electronics',
    suppliers: 38,
    otif: 86.2,
    fillRate: 92.8,
    avgLeadTime: 28.5,
    onTimeDelivery: 88.5,
    qualityScore: 93.2,
    costPerUnit: 685,
    inventoryDays: 42,
    riskScore: 'High'
  }, {
    category: 'Packaging',
    suppliers: 22,
    otif: 92.5,
    fillRate: 96.2,
    avgLeadTime: 12.5,
    onTimeDelivery: 94.8,
    qualityScore: 97.5,
    costPerUnit: 45,
    inventoryDays: 18,
    riskScore: 'Low'
  }];
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low':
        return 'bg-green-100 text-green-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'High':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div data-id="element-9981">
      <DescriptionPanel title="Supply Chain Operations Overview" description="Comprehensive view of end-to-end supply chain performance across procurement, logistics, and delivery. Monitor OTIF metrics, supplier performance, lead times, and zone-wise logistics efficiency to optimize supply chain operations." insights={['OTIF improved to 89.2% - up 2.8% from last quarter with better supplier coordination', 'Supplier fill rate at 94.5% - strong performance across all material categories', 'Average lead time reduced to 18.5 days - 2.3 days improvement through process optimization', 'North zone leading logistics performance with 91.5% OTIF and lowest lead time']} data-id="element-9982" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-9983">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-9984">
          Supply Chain Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9985">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-9986">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-9987">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-9988">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-9989">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-9990">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-9991" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-9992" />}
                {kpi.change} vs last quarter
              </div>
            </Card>)}
        </div>
      </div>
      {/* Supply Chain Flow Diagram */}
      <Card className="p-6 mb-8 bg-gradient-to-br from-blue-50 to-white" data-id="element-9993">
        <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-9994">
          Supply Chain Flow Diagram
        </h3>
        <p className="text-sm text-gray-600 mb-6" data-id="element-9995">
          End-to-end material flow from suppliers to plants
        </p>
        <div className="relative h-80" data-id="element-9996">
          <div className="flex items-center justify-between h-full" data-id="element-9997">
            {/* Stage 1: Suppliers */}
            <div className="flex flex-col justify-around h-full w-1/6" data-id="element-9998">
              <div className="text-center mb-4" data-id="element-9999">
                <div className="text-xs font-semibold text-gray-600 mb-2" data-id="element-10000">
                  SUPPLIERS
                </div>
              </div>
              <div className="space-y-3" data-id="element-10001">
                <div className="bg-blue-500 text-white p-3 rounded-lg text-center" data-id="element-10002">
                  <div className="text-xs font-semibold" data-id="element-10003">Domestic</div>
                  <div className="text-lg font-bold" data-id="element-10004">245</div>
                  <div className="text-xs" data-id="element-10005">suppliers</div>
                </div>
                <div className="bg-blue-400 text-white p-3 rounded-lg text-center" data-id="element-10006">
                  <div className="text-xs font-semibold" data-id="element-10007">Import</div>
                  <div className="text-lg font-bold" data-id="element-10008">67</div>
                  <div className="text-xs" data-id="element-10009">suppliers</div>
                </div>
              </div>
            </div>
            {/* Arrow */}
            <div className="flex items-center justify-center w-1/12" data-id="element-10010">
              <div className="text-blue-400" data-id="element-10011">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="currentColor" data-id="element-10012">
                  <path d="M20 5 L35 20 L20 35 L20 25 L5 25 L5 15 L20 15 Z" data-id="element-10013" />
                </svg>
              </div>
            </div>
            {/* Stage 2: Warehouses */}
            <div className="flex flex-col justify-around h-full w-1/6" data-id="element-10014">
              <div className="text-center mb-4" data-id="element-10015">
                <div className="text-xs font-semibold text-gray-600 mb-2" data-id="element-10016">
                  WAREHOUSES
                </div>
              </div>
              <div className="space-y-3" data-id="element-10017">
                <div className="bg-green-500 text-white p-3 rounded-lg text-center" data-id="element-10018">
                  <div className="text-xs font-semibold" data-id="element-10019">Central</div>
                  <div className="text-lg font-bold" data-id="element-10020">4</div>
                  <div className="text-xs" data-id="element-10021">locations</div>
                </div>
                <div className="bg-green-400 text-white p-3 rounded-lg text-center" data-id="element-10022">
                  <div className="text-xs font-semibold" data-id="element-10023">Regional</div>
                  <div className="text-lg font-bold" data-id="element-10024">12</div>
                  <div className="text-xs" data-id="element-10025">locations</div>
                </div>
              </div>
            </div>
            {/* Arrow */}
            <div className="flex items-center justify-center w-1/12" data-id="element-10026">
              <div className="text-green-400" data-id="element-10027">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="currentColor" data-id="element-10028">
                  <path d="M20 5 L35 20 L20 35 L20 25 L5 25 L5 15 L20 15 Z" data-id="element-10029" />
                </svg>
              </div>
            </div>
            {/* Stage 3: Plants */}
            <div className="flex flex-col justify-around h-full w-1/6" data-id="element-10030">
              <div className="text-center mb-4" data-id="element-10031">
                <div className="text-xs font-semibold text-gray-600 mb-2" data-id="element-10032">
                  PLANTS
                </div>
              </div>
              <div className="space-y-3" data-id="element-10033">
                <div className="bg-purple-500 text-white p-3 rounded-lg text-center" data-id="element-10034">
                  <div className="text-xs font-semibold" data-id="element-10035">Manesar</div>
                  <div className="text-lg font-bold" data-id="element-10036">685K</div>
                  <div className="text-xs" data-id="element-10037">units/yr</div>
                </div>
                <div className="bg-purple-400 text-white p-3 rounded-lg text-center" data-id="element-10038">
                  <div className="text-xs font-semibold" data-id="element-10039">Chennai</div>
                  <div className="text-lg font-bold" data-id="element-10040">725K</div>
                  <div className="text-xs" data-id="element-10041">units/yr</div>
                </div>
                <div className="bg-purple-300 text-white p-3 rounded-lg text-center" data-id="element-10042">
                  <div className="text-xs font-semibold" data-id="element-10043">Pune</div>
                  <div className="text-lg font-bold" data-id="element-10044">545K</div>
                  <div className="text-xs" data-id="element-10045">units/yr</div>
                </div>
              </div>
            </div>
            {/* Arrow */}
            <div className="flex items-center justify-center w-1/12" data-id="element-10046">
              <div className="text-purple-400" data-id="element-10047">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="currentColor" data-id="element-10048">
                  <path d="M20 5 L35 20 L20 35 L20 25 L5 25 L5 15 L20 15 Z" data-id="element-10049" />
                </svg>
              </div>
            </div>
            {/* Stage 4: Customers */}
            <div className="flex flex-col justify-center h-full w-1/6" data-id="element-10050">
              <div className="text-center mb-4" data-id="element-10051">
                <div className="text-xs font-semibold text-gray-600 mb-2" data-id="element-10052">
                  CUSTOMERS
                </div>
              </div>
              <div className="space-y-4" data-id="element-10053">
                <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white p-4 rounded-lg text-center" data-id="element-10054">
                  <div className="text-xs font-semibold" data-id="element-10055">OEMs</div>
                  <div className="text-2xl font-bold" data-id="element-10056">18</div>
                  <div className="text-xs mt-1" data-id="element-10057">major customers</div>
                </div>
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-4 rounded-lg text-center" data-id="element-10058">
                  <div className="text-xs font-semibold" data-id="element-10059">Aftermarket</div>
                  <div className="text-2xl font-bold" data-id="element-10060">2,500+</div>
                  <div className="text-xs mt-1" data-id="element-10061">dealers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-10062">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-10063">
            <div className="text-center" data-id="element-10064">
              <div className="text-gray-600" data-id="element-10065">Total Suppliers</div>
              <div className="text-lg font-bold text-blue-600" data-id="element-10066">312</div>
            </div>
            <div className="text-center" data-id="element-10067">
              <div className="text-gray-600" data-id="element-10068">Warehouse Locations</div>
              <div className="text-lg font-bold text-green-600" data-id="element-10069">16</div>
            </div>
            <div className="text-center" data-id="element-10070">
              <div className="text-gray-600" data-id="element-10071">Manufacturing Plants</div>
              <div className="text-lg font-bold text-purple-600" data-id="element-10072">4</div>
            </div>
            <div className="text-center" data-id="element-10073">
              <div className="text-gray-600" data-id="element-10074">End Customers</div>
              <div className="text-lg font-bold text-orange-600" data-id="element-10075">2,518</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Lead Time Trend and Zone Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-10076">
        {/* Lead Time Trend */}
        <Card className="p-6" data-id="element-10077">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-10078">
            Lead Time Trend Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10079">
            Monthly lead time performance (days)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-10080">
            <LineChart data={leadTimeTrendData} data-id="element-10081">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-10082" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-10083" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[0, 40]} data-id="element-10084" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)} days`} data-id="element-10085" />
              <Legend data-id="element-10086" />
              <Line type="monotone" dataKey="avgLeadTime" stroke="#3b82f6" strokeWidth={3} name="Average Lead Time" dot={{
              r: 5
            }} data-id="element-10087" />
              <Line type="monotone" dataKey="domesticLeadTime" stroke="#10b981" strokeWidth={2} name="Domestic" dot={{
              r: 4
            }} data-id="element-10088" />
              <Line type="monotone" dataKey="importLeadTime" stroke="#ef4444" strokeWidth={2} name="Import" dot={{
              r: 4
            }} data-id="element-10089" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-10090">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-10091">
              <div className="text-center" data-id="element-10092">
                <div className="text-gray-600" data-id="element-10093">Current Avg</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-10094">18.5d</div>
              </div>
              <div className="text-center" data-id="element-10095">
                <div className="text-gray-600" data-id="element-10096">Domestic</div>
                <div className="text-xl font-bold text-green-600" data-id="element-10097">12.8d</div>
              </div>
              <div className="text-center" data-id="element-10098">
                <div className="text-gray-600" data-id="element-10099">Import</div>
                <div className="text-xl font-bold text-red-600" data-id="element-10100">29.5d</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Zone-wise Logistics Performance */}
        <Card className="p-6" data-id="element-10101">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-10102">
            Zone-wise Logistics Performance
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10103">
            OTIF and fill rate by region (%)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-10104">
            <ComposedChart data={zoneLogisticsData} data-id="element-10105">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-10106" />
              <XAxis dataKey="zone" tick={{
              fontSize: 12
            }} data-id="element-10107" />
              <YAxis yAxisId="left" tick={{
              fontSize: 12
            }} domain={[80, 100]} data-id="element-10108" />
              <YAxis yAxisId="right" orientation="right" tick={{
              fontSize: 12
            }} domain={[10, 20]} data-id="element-10109" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: any, name: string) => {
              if (name === 'Lead Time (days)') return `${value} days`;
              return `${value}%`;
            }} data-id="element-10110" />
              <Legend data-id="element-10111" />
              <Bar yAxisId="left" dataKey="otif" fill="#3b82f6" name="OTIF %" data-id="element-10112" />
              <Bar yAxisId="left" dataKey="fillRate" fill="#10b981" name="Fill Rate %" data-id="element-10113" />
              <Line yAxisId="right" type="monotone" dataKey="leadTime" stroke="#f59e0b" strokeWidth={3} name="Lead Time (days)" dot={{
              r: 5
            }} data-id="element-10114" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-10115">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-10116">
              {zoneLogisticsData.map((zone, index) => <div key={index} className="text-center" data-id="element-10117">
                  <div className="text-gray-600" data-id="element-10118">{zone.zone}</div>
                  <div className="text-lg font-bold text-blue-600" data-id="element-10119">
                    {zone.otif}%
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-10120">
                    {zone.leadTime}d lead time
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
      </div>
      {/* Supply Chain Performance Summary Table */}
      <Card className="p-6" data-id="element-10121">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-10122">
          Supply Chain Performance Summary by Category
        </h3>
        <div className="overflow-x-auto" data-id="element-10123">
          <table className="w-full" data-id="element-10124">
            <thead data-id="element-10125">
              <tr className="border-b-2 border-gray-300" data-id="element-10126">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10127">
                  Category
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10128">
                  Suppliers
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10129">
                  OTIF %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10130">
                  Fill Rate %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10131">
                  Avg Lead Time
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10132">
                  On-Time %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10133">
                  Quality Score
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10134">
                  Cost/Unit
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10135">
                  Inventory Days
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-10136">
                  Risk Score
                </th>
              </tr>
            </thead>
            <tbody data-id="element-10137">
              {performanceSummaryData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-10138">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-10139">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-10140">
                    {row.suppliers}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-10141">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.otif >= 90 ? 'bg-green-100 text-green-700' : row.otif >= 85 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-10142">
                      {row.otif}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-10143">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.fillRate >= 95 ? 'bg-green-100 text-green-700' : row.fillRate >= 90 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-10144">
                      {row.fillRate}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-10145">
                    {row.avgLeadTime} days
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-medium text-blue-600" data-id="element-10146">
                    {row.onTimeDelivery}%
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-medium text-green-600" data-id="element-10147">
                    {row.qualityScore}%
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-700" data-id="element-10148">
                    ₹{row.costPerUnit}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-10149">
                    {row.inventoryDays} days
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-10150">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${getRiskColor(row.riskScore)}`} data-id="element-10151">
                      {row.riskScore}
                    </span>
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-10152">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-10153">AVERAGE</td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-10154">
                  62
                </td>
                <td className="py-3 px-4 text-center" data-id="element-10155">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-10156">
                    89.6%
                  </span>
                </td>
                <td className="py-3 px-4 text-center" data-id="element-10157">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-10158">
                    94.5%
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-10159">
                  19.5 days
                </td>
                <td className="py-3 px-4 text-center text-sm text-blue-600" data-id="element-10160">
                  91.8%
                </td>
                <td className="py-3 px-4 text-center text-sm text-green-600" data-id="element-10161">
                  95.4%
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-10162">
                  ₹316
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-10163">
                  31 days
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-10164">
                  -
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-10165">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-10166">
            <div className="text-center" data-id="element-10167">
              <div className="text-gray-600" data-id="element-10168">Total Suppliers</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-10169">312</div>
            </div>
            <div className="text-center" data-id="element-10170">
              <div className="text-gray-600" data-id="element-10171">Avg OTIF</div>
              <div className="text-xl font-bold text-green-600" data-id="element-10172">89.6%</div>
            </div>
            <div className="text-center" data-id="element-10173">
              <div className="text-gray-600" data-id="element-10174">Avg Fill Rate</div>
              <div className="text-xl font-bold text-green-600" data-id="element-10175">94.5%</div>
            </div>
            <div className="text-center" data-id="element-10176">
              <div className="text-gray-600" data-id="element-10177">Avg Lead Time</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-10178">19.5 days</div>
            </div>
            <div className="text-center" data-id="element-10179">
              <div className="text-gray-600" data-id="element-10180">High Risk Categories</div>
              <div className="text-xl font-bold text-red-600" data-id="element-10181">1</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default SupplyChainOverviewDashboard;